/**
 * Google and Apple sign-in on the website: the providers' own scripts, loaded
 * only when an auth card offers their button (authSeam.tsx), never on a plain
 * page view.
 *
 * Both hand back an ID TOKEN, the same thing the mobile app sends, so the
 * server's POST /api/auth/oauth/google and /apple verify it exactly as they
 * verify the app's (routes/oauth.ts).
 *
 *  - GOOGLE: Google Identity Services draws Google's own button (an iframe
 *    from accounts.google.com/gsi/); a tap opens Google's pop-up and the
 *    callback receives { credential }, the ID token. GIS gives an ID token
 *    only through its own button or One Tap, which is why this button is
 *    Google's and not drawn by app.js.
 *  - APPLE: Sign in with Apple JS with usePopup. AppleID.auth.signIn() opens
 *    appleid.apple.com in a pop-up and resolves { authorization: { id_token,
 *    state }, user? }. `user` (name, email) comes only the FIRST time a person
 *    authorizes; after that the email is in the token itself, which is what
 *    the server reads. signIn() must be called inside the tap, or the browser
 *    blocks the pop-up, so the script is preloaded when the button is shown.
 *
 * CSP (src/lib/csp.ts): these two scripts by exact URL, plus the frame, style
 * and connect paths GIS documents. Apple's script opens a window and listens
 * for appleid.apple.com's message, and needs nothing else.
 */

export const GIS_SRC = "https://accounts.google.com/gsi/client";
export const APPLE_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const loading = new Map<string, Promise<void>>();

/** One <script> per URL for the life of the page; a failed load is forgotten so the next try loads it again. */
export function loadScript(src: string): Promise<void> {
  const known = loading.get(src);
  if (known) return known;
  const p = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () => {
      loading.delete(src);
      s.remove();
      reject(new Error(`could not load ${src}`));
    });
    document.head.appendChild(s);
  });
  loading.set(src, p);
  return p;
}

/** Test seam only. */
export function __resetOauthScriptsForTests(): void {
  loading.clear();
  gisClientId = "";
  onGoogleToken = null;
}

type Gis = {
  accounts: {
    id: {
      initialize: (o: Record<string, unknown>) => void;
      renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
    };
  };
};

/* GIS takes ONE callback per initialize, and app.js repaints its cards often,
   so the callback always hands the token to the most recently mounted card. */
let gisClientId = "";
let onGoogleToken: ((idToken: string) => void) | null = null;

/** Draw Google's button into `el`; `onIdToken` gets the ID token when the person picks an account. */
export function mountGoogleButton(el: HTMLElement, clientId: string, onIdToken: (idToken: string) => void): void {
  onGoogleToken = onIdToken;
  loadScript(GIS_SRC)
    .then(() => {
      const gis = (window as unknown as { google?: Gis }).google;
      if (!gis?.accounts?.id || !el.isConnected) return;
      if (gisClientId !== clientId) {
        gis.accounts.id.initialize({
          client_id: clientId,
          callback: (r: { credential?: string }) => {
            if (r && typeof r.credential === "string" && r.credential) onGoogleToken?.(r.credential);
          },
          ux_mode: "popup",
          auto_select: false,
        });
        gisClientId = clientId;
      }
      /* GIS sizes its button in pixels, 200 to 400 */
      const measured = Math.round(el.getBoundingClientRect().width);
      const width = Math.max(200, Math.min(400, measured || 320));
      gis.accounts.id.renderButton(el, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "center",
        width,
      });
    })
    .catch(() => {
      /* blocked or offline: no button rather than a dead one */
      el.hidden = true;
    });
}

export function preloadApple(): void {
  loadScript(APPLE_SRC).catch(() => {});
}

type AppleJs = {
  auth: {
    init: (o: Record<string, unknown>) => void;
    signIn: () => Promise<{
      authorization?: { id_token?: string; state?: string };
      user?: { email?: string; name?: { firstName?: string; lastName?: string } };
    }>;
  };
};

/** What POST /api/auth/oauth/apple takes, as the mobile app sends it (mobile/lib/oauth.ts). */
export type AppleCredential = {
  identityToken: string;
  fullName: { givenName?: string; familyName?: string } | null;
  email: string | null;
};

const APPLE_FAILED = "Sign in with Apple failed. Please try again.";

function randomState(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

/**
 * Apple's pop-up. Resolves the credential, `{ cancelled: true }` when the
 * person closed it, or `{ error }`. With the script already loaded (preloadApple)
 * the pop-up opens before the first await, inside the tap.
 */
export async function appleSignIn(clientId: string): Promise<AppleCredential | { cancelled: true } | { error: string }> {
  if (!(window as unknown as { AppleID?: AppleJs }).AppleID) {
    try {
      await loadScript(APPLE_SRC);
    } catch {
      return { error: "Sign in with Apple could not load. Check your connection and try again." };
    }
  }
  const apple = (window as unknown as { AppleID?: AppleJs }).AppleID;
  if (!apple?.auth) return { error: APPLE_FAILED };
  const state = randomState();
  let data: Awaited<ReturnType<AppleJs["auth"]["signIn"]>>;
  try {
    /* the Return URL registered on the Services ID: the site's own origin */
    apple.auth.init({ clientId, scope: "name email", redirectURI: `${window.location.origin}/`, state, usePopup: true });
    data = await apple.auth.signIn();
  } catch (e) {
    const code = (e as { error?: unknown } | null)?.error;
    if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") return { cancelled: true };
    return { error: APPLE_FAILED };
  }
  const token = data?.authorization?.id_token;
  if (!token || data.authorization?.state !== state) return { error: APPLE_FAILED };
  const name = data.user?.name;
  return {
    identityToken: token,
    fullName: name ? { givenName: name.firstName, familyName: name.lastName } : null,
    email: data.user?.email ?? null,
  };
}
